/**
 * 送信元メールアカウント (mail_accounts) 用 hooks（Phase 26）。
 * 仕様: 複数アドレス + Gmail OAuth + SMTP の統合管理。
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';

import type { MailAccountProvider, MailAccountStatus } from '@goenhub/shared';

import { functions } from '@/lib/firebase';

export interface MailAccountRecord {
  id: string;
  displayName: string;
  email: string;
  provider: MailAccountProvider;
  status: MailAccountStatus;
  isDefault: boolean;
  consecutiveFailures: number;
  lastError: string | null;
  dailySendLimit: number | null;
  createdAt: string | null;
  updatedAt: string | null;
}

const listMailAccountsFn = httpsCallable<
  { dealId?: string | null },
  { accounts: MailAccountRecord[] }
>(functions, 'listMailAccounts');

/**
 * 送信元アカウント一覧。
 * 外部SA (level=external) はサーバ側で「自動メール既定の 1 件」に絞られて返る。
 * dealId を渡すとカテゴリ別送信元まで加味して解決される。
 */
export function useMailAccounts(dealId?: string | null) {
  return useQuery({
    queryKey: ['mail_accounts', dealId ?? null],
    queryFn: async (): Promise<MailAccountRecord[]> => {
      const res = await listMailAccountsFn(dealId ? { dealId } : {});
      return res.data.accounts;
    },
    staleTime: 30 * 1000,
  });
}

// ─────────────────────────────────────────────
// Gmail OAuth 開始（新規 or 再認証）
// ─────────────────────────────────────────────
const gmailOAuthStartFn = httpsCallable<
  { accountId?: string; displayName?: string },
  { authUrl: string; accountId: string }
>(functions, 'gmailOAuthStart');

export function useStartGmailOAuth() {
  return useMutation({
    mutationFn: async (input: { accountId?: string; displayName?: string }) => {
      const res = await gmailOAuthStartFn(input);
      return res.data;
    },
  });
}

// ─────────────────────────────────────────────
// SMTP 追加
// ─────────────────────────────────────────────
export interface CreateSmtpInput {
  displayName: string;
  email: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  fromName?: string;
  setAsDefault?: boolean;
}

const createSmtpAccountFn = httpsCallable<CreateSmtpInput, { ok: boolean; accountId: string }>(
  functions,
  'createSmtpAccount',
);

export function useCreateSmtpAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSmtpInput) => (await createSmtpAccountFn(input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_accounts'] }),
  });
}

// ─────────────────────────────────────────────
// SMTP 更新
// ─────────────────────────────────────────────
export interface UpdateSmtpInput {
  accountId: string;
  displayName: string;
  email: string;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password?: string;
  fromName?: string;
}

const updateSmtpAccountFn = httpsCallable<UpdateSmtpInput, { ok: boolean }>(
  functions,
  'updateSmtpAccount',
);

export function useUpdateSmtpAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateSmtpInput) => (await updateSmtpAccountFn(input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_accounts'] }),
  });
}

// ─────────────────────────────────────────────
// 削除
// ─────────────────────────────────────────────
const deleteMailAccountFn = httpsCallable<{ accountId: string }, { ok: boolean }>(
  functions,
  'deleteMailAccount',
);

export function useDeleteMailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => (await deleteMailAccountFn({ accountId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_accounts'] }),
  });
}

// ─────────────────────────────────────────────
// 既定切り替え
// ─────────────────────────────────────────────
const setDefaultMailAccountFn = httpsCallable<{ accountId: string }, { ok: boolean }>(
  functions,
  'setDefaultMailAccount',
);

export function useSetDefaultMailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => (await setDefaultMailAccountFn({ accountId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_accounts'] }),
  });
}

// ─────────────────────────────────────────────
// Gmail OAuth 解除
// ─────────────────────────────────────────────
const revokeGmailAuthFn = httpsCallable<{ accountId: string }, { ok: boolean }>(
  functions,
  'revokeGmailAuth',
);

export function useRevokeGmailAuth() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId: string) => (await revokeGmailAuthFn({ accountId })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mail_accounts'] }),
  });
}

// ─────────────────────────────────────────────
// テスト送信
// ─────────────────────────────────────────────
const testMailAccountFn = httpsCallable<
  { accountId: string; toEmail: string },
  { ok: boolean; messageId: string; provider: MailAccountProvider }
>(functions, 'testMailAccount');

export function useTestMailAccount() {
  return useMutation({
    mutationFn: async (input: { accountId: string; toEmail: string }) =>
      (await testMailAccountFn(input)).data,
  });
}
